import React, { useContext, useState, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { ReactComponent as ShowIcon } from 'assets/img/hide-icon-1.svg'
import { ReactComponent as HideIcon } from 'assets/img/hide-icon-2.svg'
import { ReactComponent as CorrectAnswerIcon } from 'assets/icons/correct-answer.svg'
import { ReactComponent as IncorrectAnswerIcon } from 'assets/icons/incorrect-answer.svg'
import { QuestionnaireContext } from '../Assessment/QuestionnaireContext'
import HtmlParser from 'components/Assessment/HtmlParser'
import { useAppSelector } from 'app/hooks'
import { SelectAssignmentState } from 'app/reducer/assignmentSlice'
import clsx from 'clsx'

interface IChoice {
  choiceText: string
  choice: string
  option: number
  answer: number
  onClick?: () => void
  disable?: boolean
  choiceId?: string
  studentAnswer?: number
  showIcons?: boolean
  hidden?: boolean
  preview?: boolean
  onHiddenChange?: (hidden: boolean) => void
}

const QuestionChoiceContainer = styled.span.attrs<{
  $fill?: boolean
  $disable?: boolean
}>((props) => ({
  className: clsx('questions__question-suggest', {
    'preserve-styles': props.$fill
  })
}))<{ $fill?: boolean; $disable?: boolean }>`
  && {
    background-color: ${(props) => (props.$fill ? '#537ab7' : 'transparent')};
    color: ${(props) => (props.$fill ? 'white' : '#537ab7')};
    opacity: ${(props) => (props.$disable ? '0.5' : '1')};
    cursor: ${(props) => (props.$disable ? 'not-allowed' : 'pointer')};
  }

  ${(props) =>
    props.$fill &&
    css`
      background-color: #537ab7 !important;
      color: #fff !important;
      border-color: #537ab7 !important;
      -webkit-text-fill-color: #fff !important;
    `}
`

const CorrectQuestionChoiceContainer = styled.span.attrs({
  className: 'questions__question-suggest preserve-styles'
})`
  background-color: ${(props) => props.theme.colors.icons.correct};
  color: white;
  border: none;
`

const IncorrectQuestionChoiceContainer = styled.span.attrs({
  className: 'questions__question-suggest preserve-styles'
})`
  background-color: ${(props) => props.theme.colors.icons.incorrect};
  color: white;
  border: none;
`

/**
 *
 * answer = actual correct answer for a question, could be 1,2,3,4
 * option = current option number for a choice component, could be 1,2,3,4
 * choice = option code for a choice component, could be A,B,C,D
 * choiceText = text for a choice component, could be anything
 */
const Choice: React.FC<IChoice> = ({
  choice,
  choiceText,
  option,
  answer,
  choiceId,
  onClick,
  disable,
  showIcons = false,
  studentAnswer,
  hidden,
  preview,
  onHiddenChange
}) => {
  const { currentQuestion } = useContext(QuestionnaireContext)
  const { enableStudentSubmissionView } = useAppSelector(SelectAssignmentState)

  const [hide, setHide] = useState(false)

  /**
   * A bulk hide or show action resets every choice through `hidden`, while the
   * local state remains the source of truth for the next individual toggle.
   * This also reports individual changes so the bulk button stays in sync.
   */
  useEffect(() => {
    setHide(Boolean(hidden))
  }, [currentQuestion, hidden])

  const toggleHide = () => {
    const nextHide = !hide

    setHide(nextHide)
    onHiddenChange?.(nextHide)
  }

  const containsImageWithDimensions = (htmlString: string) => {
    const regex =
      /<img[^>]*(\b(width|height)\s*=\s*["'][^"']*["']|\bstyle\s*=\s*["'][^"']*\b(width|height)\s*:\s*\d+[^;]*;?["'])/
    return regex.test(htmlString)
  }

  const hasStudentAnswer =
    typeof studentAnswer === 'number' && !Number.isNaN(studentAnswer)
  /**
   * Submitted A2 review questions without a student response should use
   * review feedback icons instead of the interactive show and hide controls.
   */
  const isUnansweredReview = disable && !hasStudentAnswer
  const choiceSpeakKey = choiceId || `choice_${option + 1}`

  const renderChoiceContainer = () => {
    if (!hasStudentAnswer) {
      return (
        <QuestionChoiceContainer
          $disable={disable}
          $fill={preview && answer === option}
          onClick={onClick}
        >
          {choice}
        </QuestionChoiceContainer>
      )
    }

    if (option === answer) {
      return (
        <CorrectQuestionChoiceContainer>
          {choice}
        </CorrectQuestionChoiceContainer>
      )
    }

    if (option === studentAnswer) {
      return (
        <IncorrectQuestionChoiceContainer>
          {choice}
        </IncorrectQuestionChoiceContainer>
      )
    }

    return <QuestionChoiceContainer>{choice}</QuestionChoiceContainer>
  }

  const renderChoiceStatusIcon = () => {
    if (!showIcons) {
      return null
    }

    if (isUnansweredReview) {
      return option === answer ? (
        <span className='questions__question-showHide questions__question-icon'>
          <IncorrectAnswerIcon />
        </span>
      ) : (
        <span className='questions__question-showHide questions__question-icon' />
      )
    }

    if (hasStudentAnswer) {
      if (option === answer) {
        return (
          <span className='questions__question-showHide questions__question-icon'>
            <CorrectAnswerIcon />
          </span>
        )
      }

      if (option === studentAnswer && studentAnswer !== answer) {
        return (
          <span className='questions__question-showHide questions__question-icon'>
            <IncorrectAnswerIcon />
          </span>
        )
      }

      return (
        <span className='questions__question-showHide questions__question-icon' />
      )
    }

    return (
      <button className='questions__question-icon' type='button'>
        {hide ? (
          <HideIcon onClick={toggleHide} />
        ) : (
          <ShowIcon onClick={toggleHide} />
        )}
      </button>
    )
  }

  return (
    <>
      <div className='questions__question-icons'>
        {renderChoiceStatusIcon()}
        {renderChoiceContainer()}
      </div>
      {!hide && (
        <p
          className={clsx(
            {
              'contrast-area': enableStudentSubmissionView
            },

            `questions__question-choice ${
              containsImageWithDimensions(choiceText)
                ? 'questions__question-unsetMaxHeight'
                : ''
            }`
          )}
        >
          <HtmlParser html={choiceText} id={choiceSpeakKey} />
        </p>
      )}
    </>
  )
}

export default Choice
